// ============================================================
// 📁 آدرس این فایل در پروژه (جایگزین فایل قبلی):
//    src/pages/tracking/TransitSales.tsx
//
// «فروش، رسید مشتری و سود/زیان» برای هر پرونده — هم داخل صفحه‌ی پرونده و هم در منوی گدام ← «رسید مشتری»
//   • «فروش جدید» / «ویرایش فروش»: همان مودال فروش مدیریت مالی (مشتری، تاریخ، تخفیف، آیتم‌ها، موتر، بارنامه...)
//     فروش واقعی ثبت می‌شود (گدام، حساب درآمد، حساب مشتری) و خودکار به پرونده وصل می‌شود
//   • منطق قیمت تمام‌شده: مجموع هزینه‌ها ÷ مقدار واقعاً رسیده (رسیده در گدام − کسری مشتری‌ها)
//   • کسری رسید مشتری از حساب مشتری کم می‌شود؛ سود کل و سود هر واحد (تن، کیلو، ...) روشن نمایش داده می‌شود
//   • ویرایش رسید مشتری، ویرایش فروش و حذف فروش فقط با صلاحیت «تأیید»
// ============================================================

import React, { useEffect, useState, useCallback } from "react";
import { transitAPI, journalAPI, customersAPI } from "../../services/api";
import { Overlay, Lbl, inputSt, FONT, fmt, jd, uploadAttachment, AttLink } from "./TransitExpenses";
import { Modal, Lbl as JLbl, inputSt as jInput, addBtnSt, iconBtnSt, BtnCancel } from "../finance/journal/Purchase";
import AttachmentField from "../../components/AttachmentField";
import SearchableSelect from "../../components/SearchableSelect";
import PersianDatePicker, { todayGregorian } from "../../utils/PersianDatePicker";
import { X, Plus, Trash2 } from "lucide-react";

const P2E = (v: any) => String(v ?? "").replace(/[۰-۹]/g, c => String("۰۱۲۳۴۵۶۷۸۹".indexOf(c))).replace(/[٠-٩]/g, c => String("٠١٢٣٤٥٦٧٨٩".indexOf(c)));
const num = (v: any) => P2E(v).replace(/[^0-9.]/g, "");
const f2 = (n: any) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
const r2 = (n: number) => Math.round(n * 100) / 100;

export const saleBtn = (bg: string, extra: React.CSSProperties = {}): React.CSSProperties => ({ padding: "9px 16px", borderRadius: 10, border: "none", background: bg, color: "#fff", cursor: "pointer", fontSize: 12.5, fontWeight: 800, fontFamily: FONT, ...extra });
const btn = saleBtn;
const outBtn = (T: any, extra: React.CSSProperties = {}): React.CSSProperties => ({ padding: "9px 16px", borderRadius: 10, border: `1.5px solid ${T.cardBorder}`, background: "transparent", color: T.text, cursor: "pointer", fontSize: 12.5, fontWeight: 800, fontFamily: FONT, ...extra });

const custName = (c: any) => `${c.first_name || ""} ${c.last_name || ""} ${c.company_name || ""}`.replace(/\s+/g, " ").trim();

interface Row { key: string; quantity: string; unit_price: string; }

// ───────────── مودال فروش (همان مودال فروش مدیریت مالی) ─────────────
export function CaseSaleModal({ c, editSaleId, links, onClose, onSaved, T, dark, showToast }: any) {
  const stock: any[] = c.stock || [];
  const [customers, setCustomers] = useState<any[]>([]);
  const [loaded, setLoaded] = useState<boolean>(!editSaleId);
  const [own, setOwn] = useState<any[]>([]);                 // آیتم‌های همین فروش (فقط هنگام ویرایش)
  const [customerId, setCustomerId] = useState<string>("");
  const [date, setDate] = useState<string>(todayGregorian());
  const [discount, setDiscount] = useState<string>("0");
  const [rows, setRows] = useState<Row[]>([]);
  const [notes, setNotes] = useState<string>("");
  const [plate, setPlate] = useState<string>("");
  const [waybill, setWaybill] = useState<string>("");
  const [att, setAtt] = useState<{ path: string; name: string }>({ path: "", name: "" });
  const [rate, setRate] = useState<string>("");
  const [busy, setBusy] = useState<boolean>(false);
  const [blocked, setBlocked] = useState<string>("");

  // مشتری‌ها
  useEffect(() => {
    (async () => { try { const r = await customersAPI.list(); if (r?.success) setCustomers(r.customers || []); } catch { /* بدون لیست */ } })();
  }, []);

  // بارگذاری فروش برای ویرایش
  useEffect(() => {
    if (!editSaleId) return;
    (async () => {
      try {
        const r = await journalAPI.listSales();
        const s = (r?.sales || []).find((x: any) => Number(x.id) === Number(editSaleId));
        if (!s) { setBlocked("فروش پیدا نشد"); setLoaded(true); return; }
        const prodIds = stock.map((x: any) => Number(x.product_id));
        if ((s.items || []).some((it: any) => prodIds.indexOf(Number(it.product_id)) < 0)) { setBlocked("این فروش جنس‌های دیگری هم دارد؛ لطفاً از «مدیریت مالی ← فروش» ویرایش کنید."); setLoaded(true); return; }
        setOwn(s.items || []);
        setCustomerId(String(s.customer_id)); setDate(s.date); setDiscount(String(Number(s.discount) || 0));
        setNotes(String(s.notes || "").replace(/^پرونده\s+\S+\s*—?\s*/, "")); setPlate(s.truck_plate || ""); setWaybill(s.waybill_number || "");
        setAtt({ path: s.attachment || "", name: "" });
        setRows((s.items || []).map((it: any) => ({ key: `${it.product_id}|${it.warehouse_id}`, quantity: String(Number(it.quantity)), unit_price: String(Number(it.unit_price)) })));
        const lk = (links || []).find((l: any) => Number(l.sale_id) === Number(editSaleId));
        if (lk && lk.rate_to_usd) setRate(String(Number(lk.rate_to_usd)));
      } catch { setBlocked("ارتباط با سرور برقرار نشد"); }
      setLoaded(true);
    })();
  }, [editSaleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const ownQ = (pid: number, wid: number) => own.filter((o: any) => Number(o.product_id) === pid && Number(o.warehouse_id) === wid).reduce((a: number, o: any) => a + Number(o.quantity), 0);
  const ownP = (pid: number) => own.filter((o: any) => Number(o.product_id) === pid).reduce((a: number, o: any) => a + Number(o.quantity), 0);

  // گزینه‌ها: جنس‌های پرونده × گدام‌هایی که بار در آن‌ها رسید شده
  const options: any[] = [];
  stock.forEach((s: any) => {
    (s.warehouses || []).forEach((w: any) => {
      const avail = Number(w.stock) + ownQ(Number(s.product_id), Number(w.id));
      options.push({ value: `${s.product_id}|${w.id}`, pid: Number(s.product_id), wid: Number(w.id), pname: s.product_name, wname: w.name, unit: s.unit, avail, label: `📦 ${s.product_name} — ${w.name} (موجودی ${fmt(avail)} ${s.unit})` });
    });
  });
  own.forEach((o: any) => {
    const k = `${o.product_id}|${o.warehouse_id}`;
    if (!options.some((x: any) => x.value === k)) {
      const s = stock.find((x: any) => Number(x.product_id) === Number(o.product_id));
      options.push({ value: k, pid: Number(o.product_id), wid: Number(o.warehouse_id), pname: o.product_name, wname: o.warehouse_name, unit: s ? s.unit : o.unit, avail: ownQ(Number(o.product_id), Number(o.warehouse_id)), label: `📦 ${o.product_name} — ${o.warehouse_name}` });
    }
  });

  // ردیف اول به‌صورت خودکار (برای فروش جدید)
  useEffect(() => {
    if (editSaleId || rows.length > 0 || options.length === 0) return;
    const best = options.find((o: any) => { const s = stock.find((x: any) => Number(x.product_id) === o.pid); return s && Number(s.remaining) > 0.00005 && o.avail > 0; }) || options[0];
    setRows([{ key: best.value, quantity: "", unit_price: "" }]);
  }, [options.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const cust = customers.find((x: any) => String(x.id) === customerId);
  const cur = String(cust?.currency || "USD").toUpperCase();
  useEffect(() => {
    if (cur === "USD") { setRate(""); return; }
    if (editSaleId && rate) return;
    try { const v = window.localStorage.getItem("sale_rate_" + cur); if (v) setRate(v); } catch { /* بدون ذخیره */ }
  }, [cur]); // eslint-disable-line react-hooks/exhaustive-deps

  const setRow = (i: number, p: Partial<Row>) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...p } : r));
  const addRow = () => { const best = options[0]; if (best) setRows(rs => [...rs, { key: best.value, quantity: "", unit_price: rs.length ? rs[rs.length - 1].unit_price : "" }]); };
  const delRow = (i: number) => setRows(rs => rs.filter((_, idx) => idx !== i));

  const calc = rows.map((r: Row) => {
    const opt = options.find((o: any) => o.value === r.key);
    const q = Number(r.quantity) || 0, p = Number(r.unit_price) || 0;
    return { opt, q, p, total: r2(q * p) };
  });
  const total = r2(calc.reduce((a: number, x: any) => a + x.total, 0));
  const disc = Number(discount) || 0;
  const net = r2(total - disc);
  const rt = Number(rate) || 0;
  const revUsd: number | null = cur === "USD" ? net : (rt > 0 ? r2(net / rt) : null);
  // هزینه‌ی تخمینی (قیمت تمام‌شده‌ی هر واحد × مقدار)
  let costSum = 0; let costKnown = true;
  for (const x of calc) {
    if (!x.opt) continue;
    const s = stock.find((y: any) => Number(y.product_id) === x.opt.pid);
    if (!s || s.cost_unit_usd === null || s.cost_unit_usd === undefined) { costKnown = false; break; }
    costSum += x.q * Number(s.cost_unit_usd);
  }
  const costR: number | null = costKnown ? r2(costSum) : null;
  const profit: number | null = revUsd !== null && costR !== null ? r2(revUsd - costR) : null;
  const totalQty = calc.reduce((a: number, x: any) => a + x.q, 0);
  const unitName = calc[0] && calc[0].opt ? calc[0].opt.unit : "";

  // اعتبارسنجی
  let problem = "";
  if (!customerId) problem = "مشتری الزامی است";
  else if (rows.length === 0) problem = "حداقل یک آیتم اضافه کنید";
  else if (calc.some((x: any) => !x.opt)) problem = "جنس و گدام هر آیتم را انتخاب کنید";
  else if (calc.some((x: any) => !(x.q > 0))) problem = "مقدار هر آیتم را وارد کنید";
  else if (calc.some((x: any) => !(x.p > 0))) problem = "قیمت هر آیتم را وارد کنید";
  else {
    const byP: Record<string, number> = {}; const byW: Record<string, number> = {};
    calc.forEach((x: any) => { byP[x.opt.pid] = (byP[x.opt.pid] || 0) + x.q; byW[x.opt.value] = (byW[x.opt.value] || 0) + x.q; });
    Object.keys(byP).forEach((k: string) => {
      const s = stock.find((y: any) => String(y.product_id) === k);
      const allow = (s ? Number(s.remaining) : 0) + ownP(Number(k));
      if (!problem && byP[k] > allow + 0.00005) problem = `از این پرونده فقط ${fmt(allow)} ${s ? s.unit : ""} باقی مانده است`;
    });
    Object.keys(byW).forEach((k: string) => {
      const o = options.find((x: any) => x.value === k);
      if (!problem && o && byW[k] > o.avail + 0.00005) problem = `موجودی گدام ${o.wname} فقط ${fmt(o.avail)} ${o.unit} است`;
    });
    if (!problem && cur !== "USD" && !(rt > 0)) problem = `نرخ را وارد کنید: هر ۱ دلار چند ${cur}؟`;
    if (!problem && (disc < 0 || disc > total)) problem = "تخفیف نامعتبر است";
  }

  const save = async () => {
    if (problem) { showToast(problem, false); return; }
    setBusy(true);
    try {
      let r: any;
      if (editSaleId) {
        r = await journalAPI.updateSale({
          id: editSaleId, date, customer_id: Number(customerId), customer_name: cust ? custName(cust) : "", customer_currency: cur, discount: disc,
          items: calc.map((x: any) => ({ product_id: x.opt.pid, product_name: x.opt.pname, unit: x.opt.unit, quantity: x.q, warehouse_id: x.opt.wid, warehouse_name: x.opt.wname, unit_price: x.p, total_price: x.total })),
          notes: notes ? `پرونده ${c.transit.serial} — ${notes}` : `پرونده ${c.transit.serial}`, attachment: att.path, truck_plate: plate, waybill_number: waybill, rate_to_usd: cur !== "USD" ? rt : null,
        });
      } else {
        r = await journalAPI.addSaleFromCase({
          transit_id: c.transit.id, customer_id: Number(customerId), date, discount: disc, notes, attachment: att.path, truck_plate: plate, waybill_number: waybill, rate_to_usd: cur !== "USD" ? rt : null,
          items: calc.map((x: any) => ({ product_id: x.opt.pid, warehouse_id: x.opt.wid, quantity: x.q, unit_price: x.p })),
        });
      }
      if (r?.success) {
        if (cur !== "USD") { try { window.localStorage.setItem("sale_rate_" + cur, String(rt)); } catch { /* بدون ذخیره */ } }
        showToast(editSaleId ? "فروش ویرایش شد" : `فروش ${r.serial} با موفقیت ثبت شد`, true); onSaved();
      } else showToast(r?.message || "خطا", false);
    } catch { showToast("ارتباط با سرور برقرار نشد", false); }
    setBusy(false);
  };

  const title = editSaleId ? `ویرایش فروش — پرونده ${c.transit.serial}` : `ثبت فروش جدید — پرونده ${c.transit.serial}`;
  const rowBg = dark ? "rgba(255,255,255,0.02)" : "#f8fafc";

  return (
    <Modal title={title} onClose={onClose} dark={dark} T={T}>
      {!loaded && <div style={{ padding: 30, textAlign: "center", color: T.subtext }}>در حال بارگذاری...</div>}
      {loaded && blocked && <div style={{ padding: 20, color: "#dc2626", fontWeight: 800 }}>{blocked}</div>}
      {loaded && !blocked && (
        <div dir="rtl">
          <div style={{ fontSize: 12.5, color: T.subtext, marginBottom: 16 }}>این یک فروش واقعی است: از گدام کم می‌شود، به حساب درآمد و حساب مشتری می‌رود و خودکار به همین پرونده وصل می‌ماند.</div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 20 }}>
            <div>
              <JLbl T={T}>مشتری *</JLbl>
              <SearchableSelect
                options={customers.map((x: any) => ({ value: String(x.id), label: `${custName(x)} (${x.currency || "USD"})` }))}
                value={customerId}
                onChange={(v: string) => setCustomerId(v)}
                placeholder="انتخاب مشتری"
              />
            </div>
            <div><JLbl T={T}>تاریخ</JLbl><PersianDatePicker value={date} onChange={setDate} /></div>
            <div><JLbl T={T}>تخفیف {cust ? `(${cur})` : ""}</JLbl><input value={discount} onChange={e => setDiscount(num(e.target.value))} placeholder="0" style={jInput(T, { direction: "ltr", textAlign: "right" })} inputMode="decimal" /></div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <strong style={{ fontSize: 13, color: T.text }}>آیتم‌های فروش</strong>
              <button onClick={addRow} style={addBtnSt("#10b981", true)}><Plus size={13} /> افزودن آیتم</button>
            </div>
            {rows.map((r: Row, i: number) => {
              const x = calc[i];
              return (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "3fr 1fr 90px 100px 110px 36px", gap: 8, marginBottom: 10, alignItems: "end", padding: 12, borderRadius: 10, background: rowBg, border: `1px solid ${T.border}` }}>
                  <div>
                    {i === 0 && <JLbl T={T}>محصول — گدام</JLbl>}
                    <SearchableSelect options={options.map((o: any) => ({ value: o.value, label: o.label }))} value={r.key} onChange={(v: string) => setRow(i, { key: v })} placeholder="انتخاب محصول و گدام" />
                    {x.opt && <div style={{ fontSize: 11, color: "#10b981", marginTop: 4 }}>✓ گدام: {x.opt.wname} | واحد: {x.opt.unit}</div>}
                  </div>
                  <div>
                    {i === 0 && <JLbl T={T}>واحد</JLbl>}
                    <div style={{ padding: "10px 12px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.04)" : "#fff", border: `1.5px solid ${T.border}`, color: T.text, fontSize: 13, minHeight: 42 }}>{x.opt ? x.opt.unit : "—"}</div>
                  </div>
                  <div>
                    {i === 0 && <JLbl T={T}>مقدار</JLbl>}
                    <input type="text" inputMode="decimal" value={r.quantity} onChange={e => setRow(i, { quantity: num(e.target.value) })} placeholder="0.0" style={jInput(T, { direction: "ltr", textAlign: "right" })} />
                  </div>
                  <div>
                    {i === 0 && <JLbl T={T}>قیمت هر واحد</JLbl>}
                    <input type="text" inputMode="decimal" value={r.unit_price} onChange={e => setRow(i, { unit_price: num(e.target.value) })} placeholder="0" style={jInput(T, { direction: "ltr", textAlign: "right" })} />
                  </div>
                  <div>
                    {i === 0 && <JLbl T={T}>مجموع</JLbl>}
                    <div style={{ padding: "10px 12px", borderRadius: 10, background: dark ? "rgba(16,185,129,0.1)" : "#f0fdf4", color: "#10b981", fontWeight: 700, fontSize: 13 }}>{f2(x.total)}</div>
                  </div>
                  <div style={{ marginTop: i === 0 ? 22 : 0 }}>
                    <button onClick={() => delRow(i)} style={{ ...iconBtnSt("#ef4444"), width: 36, height: 36 }}><Trash2 size={13} /></button>
                  </div>
                </div>
              );
            })}
          </div>

          {cur !== "USD" && (
            <div style={{ marginBottom: 16, padding: 14, borderRadius: 12, background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.35)", maxWidth: 420 }}>
              <JLbl T={T}>نرخ امروز: هر ۱ دلار چند {cur}؟ *</JLbl>
              <input value={rate} onChange={e => setRate(num(e.target.value))} style={jInput(T, { direction: "ltr", textAlign: "right" })} inputMode="decimal" placeholder="مثلاً 70" />
              <div style={{ fontSize: 11, color: T.subtext, marginTop: 4 }}>فقط برای حساب سود و زیان به دلار؛ حساب مشتری به همان {cur} می‌ماند.</div>
            </div>
          )}

          <div style={{ background: dark ? "rgba(16,185,129,0.08)" : "#f0fdf4", borderRadius: 12, padding: "14px 18px", marginBottom: 16, display: "flex", gap: 28, flexWrap: "wrap" }}>
            <div><span style={{ fontSize: 12, color: T.subtext }}>جمع کل: </span><strong style={{ color: T.text }}>{f2(total)}</strong></div>
            <div><span style={{ fontSize: 12, color: T.subtext }}>تخفیف: </span><strong style={{ color: "#ef4444" }}>{f2(disc)}</strong></div>
            <div><span style={{ fontSize: 12, color: T.subtext }}>خالص: </span><strong style={{ color: "#10b981", fontSize: 16 }}>{f2(net)} {cur}</strong></div>
          </div>

          {/* پیش‌نمایش سود و زیان */}
          <div style={{ padding: "14px 18px", borderRadius: 12, marginBottom: 16, border: `1.5px solid ${profit === null ? T.cardBorder : profit >= 0 ? "#10b981" : "#ef4444"}`, background: profit === null ? "transparent" : profit >= 0 ? "rgba(16,185,129,0.07)" : "rgba(239,68,68,0.07)" }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: T.subtext, marginBottom: 6 }}>👁 پیش‌نمایش سود و زیان این فروش (قبل از ثبت)</div>
            {profit !== null && totalQty > 0 ? (
              <div style={{ fontSize: 14, color: T.text, lineHeight: 2.1 }}>
                <span style={{ fontSize: 17, fontWeight: 900, color: profit >= 0 ? "#10b981" : "#ef4444" }}>{profit >= 0 ? "✅ سود" : "⛔ زیان"}: {f2(Math.abs(profit))} USD</span>
                {unitName && <span style={{ fontWeight: 800 }}> — هر {unitName}: {f2(Math.abs(profit) / totalQty)} USD</span>}
                <div style={{ fontSize: 12, color: T.subtext }}>درآمد {f2(revUsd)} − قیمت تمام‌شده {f2(costR)} (تخمینی؛ اگر مشتری کسری ثبت کند، سود دقیق دوباره حساب می‌شود)</div>
              </div>
            ) : <div style={{ fontSize: 12.5, color: T.subtext }}>{costR === null ? "قیمت تمام‌شده‌ی دلاری برای این جنس ممکن نیست." : "مقدار و قیمت را وارد کنید تا سود و زیان دیده شود."}</div>}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 16 }}>
            <div><JLbl T={T}>توضیحات</JLbl><input value={notes} onChange={e => setNotes(e.target.value)} placeholder="توضیحات..." style={jInput(T)} /></div>
            <div><JLbl T={T}>🚚 شماره موتر</JLbl><input value={plate} onChange={e => setPlate(e.target.value)} placeholder="مثلاً: 12345" style={jInput(T, { direction: "ltr" })} /></div>
            <div><JLbl T={T}>📄 شماره بارنامه</JLbl><input value={waybill} onChange={e => setWaybill(e.target.value)} placeholder="شماره بارنامه..." style={jInput(T, { direction: "ltr" })} /></div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <JLbl T={T}>ضمیمه (عکس، فایل، یا ضبط صدا)</JLbl>
            <AttachmentField value={att.path} fileName={att.name} onChange={(path: string, name: string) => setAtt({ path, name })} uploadFn={uploadAttachment} T={T} dark={dark} />
          </div>

          <div style={{ display: "flex", gap: 12, justifyContent: "space-between", alignItems: "center", marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.cardBorder}`, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12.5, color: "#dc2626", fontWeight: 700 }}>{problem}</span>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <BtnCancel onClick={onClose} T={T} />
              <button onClick={save} disabled={busy || !!problem} style={{ padding: "10px 28px", borderRadius: 10, border: "none", background: busy || problem ? "#9ca3af" : "linear-gradient(135deg,#10b981,#10b981bb)", color: "#fff", cursor: busy || problem ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, fontFamily: FONT }}>{busy ? "در حال ثبت..." : editSaleId ? "ذخیره‌ی ویرایش" : "ثبت فروش"}</button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ───────────── فرم رسید مشتری ─────────────
export function ReceiptForm({ link, onClose, onSaved, T, dark, showToast }: any) {
  const rc = link.receipt;
  const [date, setDate] = useState<string>(rc?.date || todayGregorian());
  const [qty, setQty] = useState<string>(String(rc ? Number(rc.qty) : Number(link.qty)));
  const [notes, setNotes] = useState<string>(rc?.notes || "");
  const [att, setAtt] = useState<{ path: string; name: string }>({ path: rc?.attachment || "", name: "" });
  const [busy, setBusy] = useState<boolean>(false);
  const sold = Number(link.qty);
  const diff = sold - (Number(qty) || 0);
  // مبلغی که بابت کسری از حساب مشتری کم می‌شود (تخمین برای نمایش؛ مقدار دقیق را سرور حساب می‌کند)
  const perUnitNet = sold > 0 ? (Number(link.revenue_cur) + Number(rc?.adj_amount || 0)) / sold : 0;
  const adjEst = diff > 0 ? r2(diff * perUnitNet) : 0;
  const save = async () => {
    if ((Number(qty) || 0) < 0) { showToast("مقدار نامعتبر است", false); return; }
    setBusy(true);
    try {
      const r = await journalAPI.caseReceiptSave({ link_id: link.link_id, received_date: date, received_qty: Number(qty) || 0, notes, attachment: att.path });
      if (r?.success) { showToast("رسید مشتری ثبت شد", true); onSaved(); } else showToast(r?.message || "خطا", false);
    } catch { showToast("ارتباط با سرور برقرار نشد", false); }
    setBusy(false);
  };
  return (
    <Overlay onClose={onClose} T={T} dark={dark} width={580}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 900, color: T.text }}>🧾 رسید مشتری — {link.serial}</h3>
        <button onClick={onClose} style={{ background: "none", border: "none", color: T.subtext, cursor: "pointer" }}><X size={20} /></button>
      </div>
      <div style={{ fontSize: 12.5, color: T.subtext, marginBottom: 12 }}>{link.customer} — {link.product_name}: فروخته‌شده <b>{fmt(sold)} {link.unit}</b></div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div><Lbl T={T}>تاریخ رسیدن به مشتری</Lbl><PersianDatePicker value={date} onChange={setDate} /></div>
        <div><Lbl T={T}>مقدار رسیده ({link.unit})</Lbl><input value={qty} onChange={e => setQty(num(e.target.value))} style={{ ...inputSt(T), direction: "ltr", fontSize: 16, fontWeight: 800 }} inputMode="decimal" /></div>
      </div>
      <div style={{ padding: 12, borderRadius: 10, marginBottom: 12, fontSize: 13, fontWeight: 800, lineHeight: 1.9, background: Math.abs(diff) < 0.0005 ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)", color: Math.abs(diff) < 0.0005 ? "#059669" : "#dc2626" }}>
        {Math.abs(diff) < 0.0005 ? "✓ کامل رسیده، بدون کسری" : diff > 0 ? <>کسری: {fmt(diff)} {link.unit}<br /><span style={{ fontWeight: 700, fontSize: 12 }}>مشتری فقط پول مقدار رسیده را می‌دهد: حدود {f2(adjEst)} {link.currency} از حساب مشتری کم می‌شود و قیمت تمام‌شده روی مقدار رسیده تقسیم می‌شود.</span></> : `اضافی: ${fmt(-diff)} ${link.unit} (در حساب اثری ندارد)`}
      </div>
      <div style={{ marginBottom: 12 }}><Lbl T={T}>توضیحات (اختیاری)</Lbl><textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} style={{ ...inputSt(T), resize: "vertical" }} /></div>
      <div style={{ marginBottom: 16 }}><Lbl T={T}>ضمیمه (عکس رسید، فایل، یا ضبط صدا)</Lbl><AttachmentField value={att.path} fileName={att.name} onChange={(path: string, name: string) => setAtt({ path, name })} uploadFn={uploadAttachment} T={T} dark={dark} /></div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button onClick={onClose} style={outBtn(T)}>انصراف</button>
        <button onClick={save} disabled={busy} style={btn("linear-gradient(135deg,#10b981,#059669)", { padding: "10px 28px", fontSize: 13 })}>{busy ? "..." : "ثبت رسید"}</button>
      </div>
    </Overlay>
  );
}

// ───────────── نمای کامل یک پرونده ─────────────
export function CaseSalesView({ c, canEdit, canApprove, reload, T, dark, showToast }: any) {
  const [sub, setSub] = useState<string>("summary");
  const [saleForm, setSaleForm] = useState<{ id: number | null } | null>(null);
  const [rcLink, setRcLink] = useState<any>(null);
  const [closing, setClosing] = useState<boolean>(false);
  const [cnotes, setCnotes] = useState<string>("");

  const sales = c.sales; const t = sales.totals; const closed = !!c.closed;
  const links: any[] = sales.links || [];
  const editable = !!canEdit && !closed;
  const approver = !!canApprove && !closed;
  const profit = Number(t.profit_usd);
  const profitColor = profit >= 0 ? "#10b981" : "#ef4444";
  const anyStock = (c.stock || []).some((s: any) => Number(s.remaining) > 0.00005 && (s.warehouses || []).length > 0);
  const nothingReceived = (c.stock || []).every((s: any) => Number(s.received) <= 0);
  const confirmed = links.filter((l: any) => l.sale_status === "confirmed");
  const noRc = confirmed.filter((l: any) => !l.receipt);
  const surface = dark ? "rgba(255,255,255,0.03)" : "#fff";

  // قدم بعدی به زبان ساده
  let guide = ""; let guideColor = "#2563eb";
  if (closed) { guide = "✓ این پرونده بسته شده است. گزارش نهایی سود و زیان پایین دیده می‌شود."; guideColor = "#64748b"; }
  else if (nothingReceived) { guide = "قدم بعدی: در منوی گدام ← «رسید جنس» بار را رسید کنید. بعد از آن می‌توانید بفروشید."; guideColor = "#f59e0b"; }
  else if (anyStock) { guide = "قدم بعدی: دکمه‌ی سبز «➕ فروش جدید» را بزنید و مشتری، مقدار و قیمت را بنویسید."; guideColor = "#10b981"; }
  else if (noRc.length > 0) { guide = `قدم بعدی: برای ${noRc.length} فروش، بعد از رسیدن بار به مشتری «🧾 رسید مشتری» را ثبت کنید.`; guideColor = "#f59e0b"; }
  else { guide = "همه‌چیز کامل است. قدم آخر: پرونده را «🔒 ببندید» (نیاز به صلاحیت تأیید)."; guideColor = "#10b981"; }

  const delSale = async (l: any) => {
    if (!window.confirm(`فروش ${l.serial} حذف شود؟\nموجودی گدام، حساب درآمد و حساب مشتری به حالت قبل برمی‌گردد.`)) return;
    try { const r = await journalAPI.deleteSale(l.sale_id); if (r?.success) { showToast("فروش حذف شد", true); reload(); } else showToast(r?.message || "خطا", false); }
    catch { showToast("ارتباط با سرور برقرار نشد", false); }
  };
  const delReceipt = async (l: any) => {
    if (!window.confirm("رسید مشتری حذف شود؟ (کسری که از حساب مشتری کم شده بود دوباره برمی‌گردد)")) return;
    try { const r = await journalAPI.caseReceiptDelete(l.link_id); if (r?.success) { showToast("رسید حذف شد", true); reload(); } else showToast(r?.message || "خطا", false); }
    catch { showToast("ارتباط با سرور برقرار نشد", false); }
  };
  const doClose = async (force = false) => {
    const r = await transitAPI.caseClose({ transit_id: c.transit.id, notes: cnotes, force: force ? 1 : 0 });
    if (r?.success) { showToast("پرونده بسته شد", true); setClosing(false); reload(); }
    else if (r?.code === "no_receipts") { if (window.confirm(`${r.message}. با همین وضعیت بسته شود؟`)) doClose(true); }
    else showToast(r?.message || "خطا", false);
  };
  const reopen = async () => {
    if (!window.confirm("پرونده بازگشایی شود؟")) return;
    const r = await transitAPI.caseReopen(c.transit.id); if (r?.success) { showToast("بازگشایی شد", true); reload(); } else showToast(r?.message || "خطا", false);
  };

  const subTab = (id: string): React.CSSProperties => ({ padding: "10px 22px", borderRadius: 12, border: "none", cursor: "pointer", fontFamily: FONT, fontSize: 13, fontWeight: 800, background: sub === id ? "linear-gradient(135deg,#0f172a,#1e3a5f)" : "transparent", color: sub === id ? "#fff" : T.subtext, boxShadow: sub === id ? "0 6px 16px rgba(15,23,42,0.25)" : "none" });

  const rowCard = (l: any, showSale: boolean) => (
    <div key={l.link_id} style={{ padding: "14px 16px", borderRadius: 14, border: `1px solid ${T.cardBorder}`, borderRight: `5px solid ${l.receipt ? "#10b981" : "#f59e0b"}`, background: dark ? "rgba(255,255,255,0.02)" : "#fafbfc" }}>
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
        <div style={{ minWidth: 260 }}>
          <div style={{ fontSize: 14, fontWeight: 900, color: T.text }}>{l.customer} <span style={{ fontWeight: 600, color: T.subtext, fontSize: 12 }}>({l.serial} — {jd(l.date)})</span>{l.sale_status !== "confirmed" && <span style={{ color: "#dc2626", fontSize: 12 }}> — در انتظار تأیید</span>}</div>
          {showSale && (
            <div style={{ fontSize: 12.5, color: T.subtext, marginTop: 4, lineHeight: 2 }}>
              {l.product_name}: <b style={{ color: T.text }}>{fmt(l.qty)} {l.unit}</b> × {fmt(l.unit_price)} {l.currency}
              {l.currency !== "USD" && <> · نرخ {l.rate_to_usd ? fmt(l.rate_to_usd) : <span style={{ color: "#dc2626" }}>ندارد</span>}</>}
              {(l.truck_plate || l.waybill) && <> · 🚛 {l.truck_plate || "—"} / 🧾 {l.waybill || "—"}</>}
              <br />درآمد: <b style={{ color: T.text }}>{f2(l.revenue_cur)} {l.currency}</b>
              {l.revenue_usd !== null && l.currency !== "USD" && <> ({f2(l.revenue_usd)}$)</>}
              {l.cost_usd !== null && <> · قیمت تمام‌شده: <b style={{ color: T.text }}>{f2(l.cost_usd)}$</b></>}
              {l.profit_usd !== null && <> · <b style={{ color: l.profit_usd >= 0 ? "#10b981" : "#ef4444", fontSize: 13.5 }}>{l.profit_usd >= 0 ? "سود" : "زیان"} {f2(Math.abs(l.profit_usd))}$</b>{l.profit_per_unit_usd !== null && <span style={{ color: T.text }}> (هر {l.unit}: {f2(Math.abs(l.profit_per_unit_usd))}$)</span>}</>}
            </div>
          )}
          <div style={{ fontSize: 12.5, marginTop: 4, fontWeight: 700, color: l.receipt ? "#059669" : "#b45309", lineHeight: 1.9 }}>
            {l.receipt
              ? <>✓ رسید مشتری: {jd(l.receipt.date)} — رسیده {fmt(l.receipt.qty)} {l.unit}{Math.abs(l.receipt.diff) > 0.0005 ? <span style={{ color: "#dc2626" }}> ({l.receipt.diff > 0 ? "کسری" : "اضافی"} {fmt(Math.abs(l.receipt.diff))} {l.unit}{Number(l.receipt.adj_amount) > 0 ? ` — از حساب مشتری کم شد: ${f2(l.receipt.adj_amount)} ${l.currency}` : ""})</span> : " — کامل"} <AttLink path={l.receipt.attachment} /></>
              : "● رسید مشتری هنوز ثبت نشده"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {editable && !l.receipt && l.sale_status === "confirmed" && <button onClick={() => setRcLink(l)} style={btn("linear-gradient(135deg,#10b981,#059669)")}>🧾 ثبت رسید مشتری</button>}
          {approver && l.receipt && <button onClick={() => setRcLink(l)} style={btn("linear-gradient(135deg,#3b82f6,#2563eb)")}>✏ ویرایش رسید</button>}
          {approver && l.receipt && <button onClick={() => delReceipt(l)} style={btn("transparent", { color: "#dc2626", border: "1px solid #fca5a5" })}>حذف رسید</button>}
          {showSale && approver && <button onClick={() => setSaleForm({ id: l.sale_id })} style={btn("linear-gradient(135deg,#8b5cf6,#6d28d9)")}>✏ ویرایش فروش</button>}
          {showSale && approver && <button onClick={() => delSale(l)} style={btn("transparent", { color: "#dc2626", border: "1px solid #fca5a5" })}>🗑 حذف فروش</button>}
          {showSale && editable && !approver && <span style={{ fontSize: 11.5, color: T.subtext, alignSelf: "center" }}>🔒 ویرایش و حذف فقط با صلاحیت «تأیید»</span>}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {/* قدم بعدی */}
      <div style={{ padding: "14px 18px", borderRadius: 14, marginBottom: 14, background: `${guideColor}14`, border: `1.5px solid ${guideColor}`, color: T.text, fontSize: 13.5, fontWeight: 800, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span>{guide}</span>
        <span style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {editable && <button onClick={() => setSaleForm({ id: null })} disabled={!anyStock} style={btn("linear-gradient(135deg,#10b981,#059669)", { padding: "11px 24px", fontSize: 14, opacity: anyStock ? 1 : 0.4, cursor: anyStock ? "pointer" : "not-allowed", boxShadow: anyStock ? "0 6px 16px rgba(16,185,129,0.35)" : "none" })}>➕ فروش جدید</button>}
          {canApprove && !closed && <button onClick={() => { setCnotes(""); setClosing(true); }} style={btn("linear-gradient(135deg,#0f172a,#1e3a5f)", { padding: "11px 20px", fontSize: 13 })}>🔒 بستن پرونده</button>}
          {canApprove && closed && <button onClick={reopen} style={outBtn(T, { padding: "11px 20px" })}>🔓 بازگشایی پرونده</button>}
        </span>
      </div>

      {/* تب‌های داخلی */}
      <div style={{ display: "flex", gap: 6, padding: 6, borderRadius: 16, marginBottom: 16, background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)", border: `1px solid ${T.cardBorder}`, flexWrap: "wrap" }}>
        <button onClick={() => setSub("summary")} style={subTab("summary")}>📊 سود و زیان</button>
        <button onClick={() => setSub("sales")} style={subTab("sales")}>🛒 فروش‌ها ({links.length})</button>
        <button onClick={() => setSub("receipts")} style={subTab("receipts")}>🧾 رسید مشتری{noRc.length > 0 ? ` (${noRc.length} مانده)` : ""}</button>
      </div>

      {/* ───── سود و زیان ───── */}
      {sub === "summary" && (
        <div>
          <div style={{ padding: "22px 26px", borderRadius: 18, marginBottom: 16, color: "#fff", background: links.length === 0 ? "linear-gradient(135deg,#475569,#334155)" : profit >= 0 ? "linear-gradient(135deg,#059669,#047857)" : "linear-gradient(135deg,#dc2626,#991b1b)", boxShadow: "0 14px 34px rgba(0,0,0,0.22)" }}>
            <div style={{ fontSize: 13, opacity: 0.9, fontWeight: 700 }}>{links.length === 0 ? "هنوز فروشی ثبت نشده" : profit >= 0 ? "✅ سود این پرونده" : "⛔ زیان این پرونده"}</div>
            <div style={{ fontSize: 38, fontWeight: 900, direction: "ltr", textAlign: "right", marginTop: 4, letterSpacing: 0.5 }}>{profit >= 0 ? "" : "-"}{f2(Math.abs(profit))} <span style={{ fontSize: 17 }}>USD</span></div>
            <div style={{ display: "flex", gap: 26, flexWrap: "wrap", marginTop: 12, fontSize: 13, fontWeight: 700 }}>
              <span>درآمد فروش: {f2(t.revenue_usd)} USD</span>
              <span>قیمت تمام‌شده‌ی فروخته‌شده: {f2(t.cost_usd)} USD</span>
              {Number(t.shortage_qty) > 0 && <span>کسری مشتری‌ها: {fmt(t.shortage_qty)}</span>}
            </div>
          </div>

          {(sales.warnings || []).map((w: string, i: number) => <div key={i} style={{ padding: "9px 13px", borderRadius: 10, background: "rgba(245,158,11,0.12)", color: "#b45309", fontSize: 12.5, marginBottom: 6, fontWeight: 700 }}>⚠ {w}</div>)}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(340px,1fr))", gap: 14, marginTop: 10 }}>
            {(c.stock || []).map((b: any) => {
              const pu = b.profit_per_unit_usd; const pr = Number(b.profit_usd);
              const totalCost = b.cost_unit_usd !== null ? Number(b.cost_unit_usd) * Number(b.net_received) : null;
              return (
                <div key={b.product_id} style={{ padding: 20, borderRadius: 18, border: `1px solid ${T.cardBorder}`, background: surface }}>
                  <div style={{ fontSize: 15, fontWeight: 900, color: T.text, marginBottom: 12 }}>📦 {b.product_name} <span style={{ fontSize: 12, color: T.subtext, fontWeight: 700 }}>(واحد: {b.unit})</span></div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12.5, color: T.subtext, lineHeight: 1.7 }}>
                    <div>رسید شده در گدام<br /><b style={{ color: T.text, fontSize: 15 }}>{fmt(b.received)} {b.unit}</b></div>
                    <div>کسری مشتری‌ها<br /><b style={{ color: Number(b.shortage) > 0 ? "#dc2626" : T.text, fontSize: 15 }}>{fmt(b.shortage)} {b.unit}</b></div>
                    <div>مقدار واقعاً رسیده<br /><b style={{ color: T.text, fontSize: 15 }}>{fmt(b.net_received)} {b.unit}</b></div>
                    <div>فروخته‌شده / باقی‌مانده<br /><b style={{ color: T.text, fontSize: 15 }}>{fmt(b.sold)} / <span style={{ color: Number(b.remaining) < -0.0005 ? "#dc2626" : "#10b981" }}>{fmt(b.remaining)}</span></b></div>
                  </div>
                  <div style={{ margin: "14px 0", padding: 12, borderRadius: 12, background: dark ? "rgba(255,255,255,0.04)" : "#f8fafc", fontSize: 12.5, color: T.text, lineHeight: 2 }}>
                    {b.cost_unit_usd !== null ? <>قیمت تمام‌شده‌ی هر {b.unit}: <b style={{ fontSize: 14 }}>{f2(b.cost_unit_usd)} USD</b><br /><span style={{ color: T.subtext, fontSize: 11.5 }}>= مجموع هزینه ({f2(totalCost)}) ÷ مقدار واقعاً رسیده ({fmt(b.net_received)} {b.unit})</span></> : <span style={{ color: "#b45309" }}>قیمت تمام‌شده‌ی دلاری ممکن نیست (ارز خرید دلار نیست)</span>}
                  </div>
                  {Number(b.sold) > 0 && pu !== null ? (
                    <div style={{ padding: "14px 16px", borderRadius: 14, background: pr >= 0 ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)", border: `1.5px solid ${pr >= 0 ? "#10b981" : "#ef4444"}` }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: T.subtext }}>{pr >= 0 ? "سود" : "زیان"} این جنس</div>
                      <div style={{ fontSize: 24, fontWeight: 900, color: pr >= 0 ? "#10b981" : "#ef4444", direction: "ltr", textAlign: "right" }}>{pr >= 0 ? "" : "-"}{f2(Math.abs(pr))} USD</div>
                      <div style={{ fontSize: 14, fontWeight: 900, color: T.text, marginTop: 4 }}>{pr >= 0 ? "سود" : "زیان"} هر {b.unit}: <span style={{ color: pr >= 0 ? "#10b981" : "#ef4444", direction: "ltr", display: "inline-block" }}>{f2(Math.abs(pu))} USD</span></div>
                      <div style={{ fontSize: 11.5, color: T.subtext, marginTop: 4 }}>درآمد {f2(b.revenue_usd)} − قیمت تمام‌شده {f2(b.cost_usd)} (روی {fmt(b.delivered)} {b.unit} تحویل‌شده)</div>
                    </div>
                  ) : <div style={{ fontSize: 12.5, color: T.subtext, padding: 10 }}>{Number(b.sold) > 0 ? "برای سود و زیان نرخ دلار یا قیمت تمام‌شده لازم است." : "هنوز از این جنس فروخته نشده است."}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ───── فروش‌ها ───── */}
      {sub === "sales" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {links.length === 0 && <div style={{ padding: 26, textAlign: "center", color: T.subtext, fontSize: 13.5, border: `1px dashed ${T.cardBorder}`, borderRadius: 14 }}>هنوز فروشی از این پرونده ثبت نشده است.</div>}
          {links.map((l: any) => rowCard(l, true))}
        </div>
      )}

      {/* ───── رسید مشتری ───── */}
      {sub === "receipts" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {confirmed.length === 0 && <div style={{ padding: 26, textAlign: "center", color: T.subtext, fontSize: 13.5, border: `1px dashed ${T.cardBorder}`, borderRadius: 14 }}>هنوز فروش تأییدشده‌ای نیست.</div>}
          {noRc.length > 0 && <div style={{ fontSize: 13, fontWeight: 900, color: "#b45309" }}>● منتظر رسید مشتری ({noRc.length})</div>}
          {noRc.map((l: any) => rowCard(l, false))}
          {confirmed.filter((l: any) => l.receipt).length > 0 && <div style={{ fontSize: 13, fontWeight: 900, color: "#059669", marginTop: 8 }}>✓ رسید ثبت شده</div>}
          {confirmed.filter((l: any) => l.receipt).map((l: any) => rowCard(l, false))}
        </div>
      )}

      {saleForm && <CaseSaleModal c={c} editSaleId={saleForm.id} links={links} onClose={() => setSaleForm(null)} onSaved={() => { setSaleForm(null); reload(); }} T={T} dark={dark} showToast={showToast} />}
      {rcLink && <ReceiptForm link={rcLink} onClose={() => setRcLink(null)} onSaved={() => { setRcLink(null); reload(); }} T={T} dark={dark} showToast={showToast} />}
      {closing && (
        <Overlay onClose={() => setClosing(false)} T={T} dark={dark} width={500}>
          <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 900, color: T.text }}>🔒 بستن پرونده</h3>
          <div style={{ fontSize: 13, color: T.subtext, marginBottom: 12, lineHeight: 1.9 }}>بعد از بستن، فروش جدید، ویرایش و رسید مشتری قفل می‌شود (با «بازگشایی پرونده» دوباره باز می‌شود).<br />{profit >= 0 ? "سود" : "زیان"} نهایی: <b style={{ color: profitColor, direction: "ltr", display: "inline-block" }}>{f2(Math.abs(profit))} USD</b></div>
          <textarea value={cnotes} onChange={e => setCnotes(e.target.value)} rows={2} placeholder="یادداشت (اختیاری)" style={{ ...inputSt(T), resize: "vertical", marginBottom: 14 }} />
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={() => setClosing(false)} style={outBtn(T)}>انصراف</button>
            <button onClick={() => doClose(false)} style={btn("linear-gradient(135deg,#0f172a,#1e3a5f)", { padding: "10px 28px", fontSize: 13 })}>بستن پرونده</button>
          </div>
        </Overlay>
      )}
    </div>
  );
}

// ───────────── پنل داخل صفحه‌ی پرونده ─────────────
export default function SalesPanel({ transitId, canEdit, canApprove, T, dark, showToast }: any) {
  const [board, setBoard] = useState<any>(null);
  const [err, setErr] = useState<string>("");

  const load = useCallback(async () => {
    try {
      const r = await transitAPI.caseBoard(transitId);
      if (r?.success) { setBoard(r); setErr(""); } else setErr(r?.message || "خطا");
    } catch { setErr("ارتباط با سرور برقرار نشد"); }
  }, [transitId]);
  useEffect(() => { load(); }, [load]);

  const card: React.CSSProperties = { padding: 22, borderRadius: 18, border: `1px solid ${T.cardBorder}`, background: dark ? "rgba(255,255,255,0.03)" : "#fff", marginBottom: 18 };
  if (err) return <div style={card}><div style={{ color: "#dc2626", fontSize: 13 }}>{err}</div></div>;
  if (!board) return <div style={card}><div style={{ color: T.subtext, fontSize: 13 }}>در حال بارگذاری بخش فروش...</div></div>;
  const c = (board.cases || [])[0];
  if (!c) return <div style={card}><div style={{ color: T.subtext, fontSize: 13 }}>اطلاعات فروش این پرونده پیدا نشد.</div></div>;
  return (
    <div style={card}>
      <div style={{ fontSize: 16, fontWeight: 900, color: T.text, marginBottom: 14 }}>💰 فروش، رسید مشتری و سود/زیان</div>
      <CaseSalesView c={c} canEdit={canEdit !== undefined ? canEdit : !!board.can?.edit} canApprove={canApprove !== undefined ? canApprove : !!board.can?.approve} reload={load} T={T} dark={dark} showToast={showToast} />
    </div>
  );
}
